import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, matchRoutes, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import get from 'lodash/get';
import { Unity, useUnityContext } from 'react-unity-webgl';
import { CardHeader, Divider, Grid, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid';
import MasterComponent from '../../common/MasterComponent';
import Popup from '../../components/Popup';
import AddMachine from './addMachine';
import { machineryManagementActions } from '../../store/reducers/machinery-management';
import {
  MachineDataType,
  MachineTagsType,
  MachineryDataRequestPayloadTypes,
  MachineryManagementTypes,
} from '../../store/models/machine-management';
import { addMachineDefaultFields, machineExcelSchema } from './form';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../utils/table-utils';
import { isEmpty } from 'lodash';
import { ReactComponent as AlertIcon } from '../../assets/icons/main/alertIcon.svg';
import { palette } from '../../theme/palette';
import FormModal from '../../common/formModal';
import OntologySelector from '../../common/OntologySelector';
import Styles from '../../theme/commonCss/Styles';
import FileImport from '../../common/FileImport';

const StylesCustom = {
  disabledAddParts: { cursor: 'not-allowed', opacity: 0.6 },
};

const defaultPopupState: any = {
  show: false,
  title: '',
  message: '',
  confirmLabel: '',
  cancelLabel: '',
  confirmation: true,
  icon: '',
  onConfirm: null,
  cancel: true,
};

const machineDataDefault: MachineDataType = {
  id: 0,
  machine_id: 0,
  machine_name: '',
  machine_manufacturer: '',
  machine_type: '',
  part_no: null,
  machine_tags: [],
  machine_parts: [],
  onto_manage_id: null,
};

const MachineryManagement = ({
  isMachineryManagementDataLoading,
  machineryManagementData,
  machineryManagementDataTotalRowCount,
  machineryManagementDataPageNumber,
  isAddMachineSuccess,
  isAddMachineError,
  isDeleteMachineSuccess,
  isDeleteMachineError,
  isUpdateMachineSuccess,
  isUpdateMachineError,
  machineryManagementError,
  isBulkUploadMachineLoading,
  isBulkUploadMachineSuccess,
  isBulkUploadMachineError,
  machineStatusCode,
  searchItem,
  userAccess,
  machinePageName,
  settingsData,
  ontologyData,
}: MachineryManagementTypes) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [machineData, setMachineData] = useState<MachineDataType>(addMachineDefaultFields);
  const [machineTagsData, setMachineTagsData] = useState<Array<MachineTagsType>>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [machineMasterData, setMachineMasterData] = useState<Array<MachineDataType>>([]);
  const [popupData, setPopupData] = useState<any>(defaultPopupState);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const [bulkMachinesOntologyModal, setBulkMachinesOntologyModal] = useState({
    open: false,
    data: [],
    onto_manage_id: null,
  });
  const [ajvErrors, setAjvErrors] = useState<any>(false);

  useEffect(() => {
    if (machineryManagementData && machineryManagementData.length > 0) {
      setMachineMasterData(
        machineryManagementData.map((item: any) => {
          return {
            ...item,
            id: item?.machine_id,
            machine_tags:
              item?.machine_tags && item?.machine_tags.length > 0
                ? item?.machine_tags.map((val: any, index: any) => {
                  return { ...val, id: index + 1 };
                })
                : [],
          };
        })
      );
    } else {
      setMachineMasterData([]);
    }
  }, [machineryManagementData]);

  useEffect(() => {
    if (isAddMachineSuccess || isDeleteMachineSuccess || isUpdateMachineSuccess || isBulkUploadMachineSuccess) {
      dispatchMachineryDataRequest({
        limit: DEFAULT_PAGE_SIZE,
        page:
          isDeleteMachineSuccess && machineMasterData.length === 1 && machineryManagementDataPageNumber !== 1
            ? machineryManagementDataPageNumber - 1
            : machineryManagementDataPageNumber,
      });
    }

    if (machineStatusCode != 409) {
      setIsModalOpen(false);
      setIsEdit(false);
      setMachineData(machineDataDefault);
      setMachineTagsData([]);
    }
  }, [
    machineStatusCode,
    isAddMachineSuccess,
    isDeleteMachineSuccess,
    isUpdateMachineSuccess,
    isBulkUploadMachineSuccess,
  ]);

  const handleClick = (params: any, type: string) => {
    if (type === 'navigate') {
      navigate(`/assets/machinery/${params.machine_id}/parts`, { state: params });
    } else if (type === 'edit') {
      setIsEdit(true);
      setMachineData(params);
      setMachineTagsData(params.machine_tags || []);
      setIsModalOpen(true);
    } else if (type === 'delete') {
      setMachineData(params);
      setConfirmPopup(true);
    }
  };

  const handlePopupActions = (data: any, type: any) => {
    if (type === 'delete_confirm') {
      dispatch(machineryManagementActions.deleteMachineRequest(machineData));
      setMachineData(machineDataDefault);
      setConfirmPopup(false);
    } else if (type === 'delete_close') {
      setConfirmPopup(false);
      setMachineData(machineDataDefault);
    } else if (type === 'add_edit_popup') {
      setIsModalOpen(false);
      setIsEdit(false);
      setMachineData(machineDataDefault);
      setMachineTagsData([]);
    }
  };

  const [editor, setEditor] = useState({ show: false, machine: -1 });

  const showEditor = useCallback((machineId: number) => {
    setEditor({ show: true, machine: machineId });
  }, []);

  const renderPopup = () => (
    <Popup
      title={popupData?.title}
      open={popupData?.show}
      icon={popupData?.icon}
      message={popupData?.message}
      confirmation
      confirmLabel={popupData?.confirmLabel}
      onConfirm={popupData?.onConfirm}
      cancel={popupData?.cancel}
      cancelLabel={popupData?.cancelLabel}
      onCancel={popupData.onCancel ? popupData.onCancel : () => { }}
      onClose={popupData.onClose ? popupData.onClose : () => { }}
    />
  );

  const handleSubmit = (data:any) => {
    // e.preventDefault();
    if (bulkMachinesOntologyModal.onto_manage_id) {
      const machines = data.map((machine: any) => ({
        ...machine,
        onto_manage_id: bulkMachinesOntologyModal.onto_manage_id,
      }));      
      setAjvErrors(false);
      dispatch(machineryManagementActions.bulkUploadRequest(machines));
      onCloseBulkOntoModal()
    } else {
      setAjvErrors(true);
    }
  }

  const handleBulkUpload = (data: any) => {
    // setBulkMachinesOntologyModal({
    //   open: true,
    //   data,
    //   onto_manage_id: null,
    // });
    
    dispatch(machineryManagementActions.bulkUploadRequest(data));
  };

  const {
    unityProvider,
    isLoaded,
    sendMessage,
    addEventListener,
    removeEventListener,
    // UNSAFE__detachAndUnloadImmediate
    unload,
  } = useUnityContext({
    loaderUrl: '/webgl/Build/WebGL-Export.loader.js',
    dataUrl: '/webgl/Build/WebGL-Export.data',
    frameworkUrl: '/webgl/Build/WebGL-Export.framework.js',
    codeUrl: '/webgl/Build/WebGL-Export.wasm',
  });

  useEffect(() => {
    const cb = () => {
      // console.log('changing scene to machine config');
      //const json = {"machineID":"Chiller1","machineName":"Chiller","machineDescription":"","machinePath":"https://drive.google.com/uc?export=download&id=1VOCf09EdNAZ8PshOO6Hg1fHyyORtf_54","uniqueID":"-","position":{"x":0,"y":0,"z":0},"rotation":{"x":0,"y":0,"z":0},"scale":{"x":0,"y":0,"z":0},"parameters":[{"parameterID":"temperature","name":"temperature","description":"","type":"Float","unit":"degreeC","value":0,"min":3.3,"max":15.6,"idealReading":6.6},{"parameterID":"humidity","name":"humidity","description":"","type":"Float","unit":"degreeC","value":0,"min":-28.49,"max":43.4,"idealReading":9},{"parameterID":"var_entering_chilled_temp1","name":"Entering Chilled Liquid Temperature","description":"","type":"Float","unit":"degreeC","value":0,"min":-28.49,"max":43.4,"idealReading":15},{"parameterID":"var_variable_speed_drive_int_ambient_temp1","name":"Variable Speed Drive Internal Ambient Temperature","description":"","type":"Float","unit":"degreeC","value":0,"min":0,"max":70,"idealReading":61.6},{"parameterID":"var_leaving_liquid_temp1","name":"Leaving Condenser Liquid Temp","description":"","type":"Float","unit":"degreeC","value":0,"min":-7.31,"max":79.7,"idealReading":9},{"parameterID":"var_sys1_discharge_temp1","name":"System1 Discharge Temperature","description":"","type":"Float","unit":"degreeC","value":0,"min":4.61,"max":150,"idealReading":20},{"parameterID":"var_entering_liquid_temp1","name":"Entering Condenser Liquid Temperature","description":"","type":"Float","unit":"degreeC","value":0,"min":-28.49,"max":43.4,"idealReading":9},{"parameterID":"var_sys1_oil_pressure1","name":"System1 Oil Pressure","description":"","type":"Float","unit":"PSIG","value":0,"min":0,"max":400,"idealReading":130},{"parameterID":"var_sys1_evaporator_pressure1","name":"System1 Evaporator Pressure","description":"","type":"Float","unit":"PSIG","value":0,"min":0,"max":125,"idealReading":125},{"parameterID":"var_sys1_discharge_pressure1","name":"System1 Discharge Pressure","description":"","type":"Float","unit":"PSIG","value":0,"min":0,"max":400,"idealReading":131},{"parameterID":"var_sys1_compressor_full_load1","name":"System1 Compressor Full Load","description":"","type":"None","unit":"%","value":0,"min":0,"max":1,"idealReading":0},{"parameterID":"var_sys1_total_run_hours1","name":"System1 Total Run Hours","description":"","type":"None","unit":"Hr","value":0,"min":0,"max":1,"idealReading":0},{"parameterID":"var_sys1_highest_motor_temp1","name":"System1 Highest Motor Temperature","description":"","type":"Float","unit":"degreeC","value":0,"min":-34.56,"max":150,"idealReading":50},{"parameterID":"var_sys2_highest_motor_temp1","name":"System2 Highest Motor Temperature","description":"","type":"Float","unit":"degreeC","value":0,"min":-34.56,"max":150,"idealReading":50},{"parameterID":"var_sys1_economizer_pressure1","name":"System1 Economizer Pressure","description":"","type":"Float","unit":"degreeC","value":0,"min":-7.31,"max":79.7,"idealReading":48.8},{"parameterID":"var_sys2_oil_pressure1","name":"System2 Oil Pressure","description":"","type":"Float","unit":"PSIG","value":0,"min":0,"max":400,"idealReading":130},{"parameterID":"var_sys2_evaporator_pressure1","name":"System2 Evaporator Pressure","description":"","type":"Float","unit":"PSIG","value":0,"min":0,"max":125,"idealReading":125},{"parameterID":"var_sys2_discharge_pressure1","name":"System2 Discharge Pressure","description":"","type":"Float","unit":"PSIG","value":0,"min":0,"max":400,"idealReading":131},{"parameterID":"var_ambient_temperature1","name":"Ambient Temperature","description":"","type":"Float","unit":"PSIG","value":0,"min":0,"max":0,"idealReading":-1},{"parameterID":"var_remote_start_stop1","name":"Remote Start Stop","description":"","type":"Switch","unit":"","value":0,"min":0,"max":1,"idealReading":0},{"parameterID":"var_compressor_on_off1","name":"Compressor Start Stop","description":"","type":"Switch","unit":"","value":0,"min":0,"max":1,"idealReading":0},{"parameterID":"var_sys1_total_num_of_starts1","name":"System1 Total Number of Starts","description":"","type":"None","unit":"","value":0,"min":0,"max":1,"idealReading":0}],"Bubbles":[]};
      sendMessage('SceneLoader', 'LoadMachineConfigurator', '');
    };
    addEventListener('SceneLoaderReady', cb);
    return () => removeEventListener('SceneLoaderReady', cb);
  }, [sendMessage, isLoaded]);

  useEffect(() => {
    const cb = () => {
      // console.log('trying to load Machine configuration json');
      const json = {
        machineID: 'Chiller1',
        machineName: 'Chiller',
        machineDescription: '',
        machinePath: `http://${window.location.host}/webgl/Build/Parth.glb`,
        uniqueID: '-',
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 0, y: 0, z: 0 },
        parameters: [
          {
            parameterID: 'temperature',
            name: 'temperature',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: 3.3,
            max: 15.6,
            idealReading: 6.6,
          },
          {
            parameterID: 'humidity',
            name: 'humidity',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: -28.49,
            max: 43.4,
            idealReading: 9,
          },
          {
            parameterID: 'var_entering_chilled_temp1',
            name: 'Entering Chilled Liquid Temperature',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: -28.49,
            max: 43.4,
            idealReading: 15,
          },
          {
            parameterID: 'var_variable_speed_drive_int_ambient_temp1',
            name: 'Variable Speed Drive Internal Ambient Temperature',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: 0,
            max: 70,
            idealReading: 61.6,
          },
          {
            parameterID: 'var_leaving_liquid_temp1',
            name: 'Leaving Condenser Liquid Temp',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: -7.31,
            max: 79.7,
            idealReading: 9,
          },
          {
            parameterID: 'var_sys1_discharge_temp1',
            name: 'System1 Discharge Temperature',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: 4.61,
            max: 150,
            idealReading: 20,
          },
          {
            parameterID: 'var_entering_liquid_temp1',
            name: 'Entering Condenser Liquid Temperature',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: -28.49,
            max: 43.4,
            idealReading: 9,
          },
          {
            parameterID: 'var_sys1_oil_pressure1',
            name: 'System1 Oil Pressure',
            description: '',
            type: 'Float',
            unit: 'PSIG',
            value: 0,
            min: 0,
            max: 400,
            idealReading: 130,
          },
          {
            parameterID: 'var_sys1_evaporator_pressure1',
            name: 'System1 Evaporator Pressure',
            description: '',
            type: 'Float',
            unit: 'PSIG',
            value: 0,
            min: 0,
            max: 125,
            idealReading: 125,
          },
          {
            parameterID: 'var_sys1_discharge_pressure1',
            name: 'System1 Discharge Pressure',
            description: '',
            type: 'Float',
            unit: 'PSIG',
            value: 0,
            min: 0,
            max: 400,
            idealReading: 131,
          },
          {
            parameterID: 'var_sys1_compressor_full_load1',
            name: 'System1 Compressor Full Load',
            description: '',
            type: 'None',
            unit: '%',
            value: 0,
            min: 0,
            max: 1,
            idealReading: 0,
          },
          {
            parameterID: 'var_sys1_total_run_hours1',
            name: 'System1 Total Run Hours',
            description: '',
            type: 'None',
            unit: 'Hr',
            value: 0,
            min: 0,
            max: 1,
            idealReading: 0,
          },
          {
            parameterID: 'var_sys1_highest_motor_temp1',
            name: 'System1 Highest Motor Temperature',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: -34.56,
            max: 150,
            idealReading: 50,
          },
          {
            parameterID: 'var_sys2_highest_motor_temp1',
            name: 'System2 Highest Motor Temperature',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: -34.56,
            max: 150,
            idealReading: 50,
          },
          {
            parameterID: 'var_sys1_economizer_pressure1',
            name: 'System1 Economizer Pressure',
            description: '',
            type: 'Float',
            unit: 'degreeC',
            value: 0,
            min: -7.31,
            max: 79.7,
            idealReading: 48.8,
          },
          {
            parameterID: 'var_sys2_oil_pressure1',
            name: 'System2 Oil Pressure',
            description: '',
            type: 'Float',
            unit: 'PSIG',
            value: 0,
            min: 0,
            max: 400,
            idealReading: 130,
          },
          {
            parameterID: 'var_sys2_evaporator_pressure1',
            name: 'System2 Evaporator Pressure',
            description: '',
            type: 'Float',
            unit: 'PSIG',
            value: 0,
            min: 0,
            max: 125,
            idealReading: 125,
          },
          {
            parameterID: 'var_sys2_discharge_pressure1',
            name: 'System2 Discharge Pressure',
            description: '',
            type: 'Float',
            unit: 'PSIG',
            value: 0,
            min: 0,
            max: 400,
            idealReading: 131,
          },
          {
            parameterID: 'var_ambient_temperature1',
            name: 'Ambient Temperature',
            description: '',
            type: 'Float',
            unit: 'PSIG',
            value: 0,
            min: 0,
            max: 0,
            idealReading: -1,
          },
          {
            parameterID: 'var_remote_start_stop1',
            name: 'Remote Start Stop',
            description: '',
            type: 'Switch',
            unit: '',
            value: 0,
            min: 0,
            max: 1,
            idealReading: 0,
          },
          {
            parameterID: 'var_compressor_on_off1',
            name: 'Compressor Start Stop',
            description: '',
            type: 'Switch',
            unit: '',
            value: 0,
            min: 0,
            max: 1,
            idealReading: 0,
          },
          {
            parameterID: 'var_sys1_total_num_of_starts1',
            name: 'System1 Total Number of Starts',
            description: '',
            type: 'None',
            unit: '',
            value: 0,
            min: 0,
            max: 1,
            idealReading: 0,
          },
        ],
        Bubbles: [],
      };
      sendMessage('DataManager', 'loadMachineData', JSON.stringify(json));
    };
    addEventListener('MachineConfigInitComplete', cb);
    return () => removeEventListener('MachineConfigInitComplete', cb);
  }, [sendMessage, isLoaded]);

  useEffect(() => {
    const cb = () => {
      // setEditor({show: false, machine: -1});
      unload().then(() => {
        setEditor({ show: false, machine: -1 });
      });
      // UNSAFE__detachAndUnloadImmediate().then(() => {
      //   setTimeout(() => setEditor({show: false, machine: -1}), 100);
      // });
    };
    addEventListener('MachineConfigJsonExport', cb);
    return () => removeEventListener('MachineConfigJsonExport', cb);
  }, [sendMessage, isLoaded, unload]);
  // UNSAFE__detachAndUnloadImmediate

  useEffect(() => {
    return () => {
      if (isLoaded) {
        try {
          // UNSAFE__detachAndUnloadImmediate();
        } catch (e) {
          console.error(e);
        }
      }
    };
  }, [isLoaded]);

  const pageData: any = get(useOutletContext(), 'outletData', {});
  const themeData: any = useTheme();
  var pluralize = require('pluralize');

  const dispatchMachineryDataRequest: any = (payload: MachineryDataRequestPayloadTypes) => {
    dispatch(
      machineryManagementActions.getMachineryManagementDataRequest({
        ...sortingModel,
        search_item: searchItem,
        ...payload,
        pageName: get(pageData, 'entity_name', 'Machine'),
      })
    );
  };

  useEffect(() => {
    dispatchMachineryDataRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER });
    return () => {
      dispatch(machineryManagementActions.resetLoadingState());
      dispatch(machineryManagementActions.resetMachineryManagementData());
    };
  }, [settingsData]);

  const columns: any = [
    {
      field: 'machine_name',
      headerName: `${get(pageData, 'entity_name', 'Machine') + ' Name'}`,
      flex: 1,
      minWidth: 200,
      renderCell: (params: any) => {
        return (
          <div style={{ cursor: 'pointer' }}>
            <Tooltip title={params.value} placement='left' arrow>
              <Typography
                component='p'
                variant='subtitle1'
                sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
              >
                {params?.value} <br />
              </Typography>
            </Tooltip>

            <small>
              {userAccess.read ? (
                <Link
                  style={{ color: themeData.palette.primary.main }}
                  to={`/assets/machinery/${params.row.machine_id}/parts`}
                >
                  (Add {pluralize(get(pageData.things_parts, 'entity_name', 'Part'))})
                </Link>
              ) : (
                <span style={StylesCustom.disabledAddParts}>
                  Add {pluralize(get(pageData.things_parts, 'entity_name', 'Part'))}
                </span>
              )}
            </small>
          </div>
        );
      },
      sortComparator: (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true }),
    },
    {
      field: 'machine_manufacturer',
      headerName: `${get(pageData, 'entity_name', 'Machine') + ' Manufacturer'}`,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 300,
    },
    {
      field: 'machine_type',
      headerName: `${get(pageData, 'entity_name', 'Machine') + ' Type'}`,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      minWidth: 100,
    },
    {
      field: 'machine_parts',
      headerName: `${'No of ' + pluralize(get(pageData.things_parts, 'entity_name', 'Part'))}`,
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      minWidth: 100,
      sortable: false,
      renderCell: (params: any) => {
        if (params?.value) {
          return params?.value.length;
        }
        return 0;
      },
    },
  ];

  const renderFormModal = () => (
    <AddMachine
      isOpen={isModalOpen}
      machineData={machineData}
      setMachineData={setMachineData}
      machineTagsData={machineTagsData}
      setMachineTagsData={setMachineTagsData}
      machineMasterData={machineMasterData}
      isEdit={isEdit}
      closeModal={() => handlePopupActions({}, 'add_edit_popup')}
      pageData={isEmpty(pageData) ? {} : pageData}
      ontologyData={ontologyData}
    />
  );

  const onCloseBulkOntoModal = () => {
    setAjvErrors(false);
    setBulkMachinesOntologyModal({ open: false, data: [], onto_manage_id: null });
  };

  const openBulkPopup = () => {
      setBulkMachinesOntologyModal({
      open: true,
      data:[],
      onto_manage_id: null,
    });
  }

  const responsiveSize = useMediaQuery('(max-width:900px)');

  return (
    <>
      {editor.show ? (
        <Unity matchWebGLToCanvasSize={true} style={{ height: '100%', width: '100%' }} unityProvider={unityProvider} />
      ) : (
        <MasterComponent
          heading={`List of ${pluralize(get(pageData, 'entity_name', 'Machine'))}`}
          isLoading={isMachineryManagementDataLoading}
          rows={machineMasterData}
          columns={columns}
          isServerPagination
          totalRowCount={machineryManagementDataTotalRowCount}
          pageNumber={machineryManagementDataPageNumber}
          onPagination={(pageSize: number, pageNumber: number) => {
            dispatchMachineryDataRequest({
              limit: pageSize,
              page: pageNumber,
            });
          }}
          onSorting={(sortModel: { field: string; sort: string }) => {
            dispatchMachineryDataRequest({
              limit: DEFAULT_PAGE_SIZE,
              page: machineryManagementDataPageNumber,
              ...sortModel,
            });
            setSortingModel(sortModel);
          }}
          setIsModalOpen={setIsModalOpen}
          showImport={true}
          showExport={true}
          isTemplate={true}
          imporAction={handleBulkUpload}
          excelSchema={machineExcelSchema}
          dataColumns={['machine_name', 'machine_manufacturer', 'machine_type']}
          exportData={addMachineDefaultFields}
          fileName={get(pageData, 'entity_name', 'machine') + '_template'}
          entityName={get(pageData, 'entity_name', 'machine')}
          isFromMachine={true}
          noRowsOverlay={() => (
            <Stack height='100%' alignItems='center' justifyContent='center'>
              {get(pageData, 'entity_name', 'Machine')} data not available
            </Stack>
          )}
          actionsColumn={{
            edit: {
              onClick: (params: GridValueGetterParams) => handleClick(params.row, 'edit'),
            },
            settings: {
              onClick: (params: GridValueGetterParams) => showEditor(params.row.machine_id),
            },
            delete: {
              onClick: (params: GridValueGetterParams) => handleClick(params.row, 'delete'),
            },
          }}
          showSearch
          searchPlaceholder='Name / Manufacturer / Type'
          onSearch={(searchValue: string) => {
            dispatchMachineryDataRequest({
              limit: DEFAULT_PAGE_SIZE,
              page: DEFAULT_PAGE_NUMBER,
              search_item: searchValue,
            });
            dispatch(machineryManagementActions.onSearchForItem(searchValue));
          }}
          openBulkPopup={openBulkPopup}
        />
      )}
      <Popup
        open={confirmPopup}
        icon={<AlertIcon color={palette.warning.main} />}
        message={`Are you sure to delete this ${get(pageData, 'entity_name', 'machine').toLowerCase()}?`}
        cancel={true}
        confirmation={true}
        cancelLabel='No'
        confirmLabel='Yes'
        onConfirm={() => handlePopupActions({}, 'delete_confirm')}
        onClose={() => handlePopupActions({}, 'delete_close')}
        onCancel={() => handlePopupActions({}, 'delete_close')}
      />
      {isModalOpen && renderFormModal()}
      {renderPopup()}
      <FormModal
        hideButtons={true}
        isOpen={bulkMachinesOntologyModal.open}
        title='IMPORT MACHINE LIST'
        customStyles={{ width: '100%' }}
        handleClose={onCloseBulkOntoModal}
      // handleSubmit={(e: React.FormEvent) => {
      //   e.preventDefault();
      //   if (bulkMachinesOntologyModal.onto_manage_id) {
      //     const machines = bulkMachinesOntologyModal.data.map((machine: any) => ({
      //       ...machine,
      //       onto_manage_id: bulkMachinesOntologyModal.onto_manage_id,
      //     }));
      //     console.log(machines);
      //     setAjvErrors(false);
      //     // dispatch(machineryManagementActions.bulkUploadRequest());
      //     // onCloseBulkOntoModal()
      //   } else {
      //     setAjvErrors(true);
      //   }
      // }}
      >
        <CardHeader title={`Select Ontology Location`} titleTypographyProps={Styles.title} />
        <Divider />
        <Grid
          container
          spacing={1}
          textAlign={'right'}
          sx={{
            ...Styles.gridContainer,
            '&.MuiGrid-root': {
              paddingBottom: '20px !important',
            },
            marginLeft: 0,
            marginTop: responsiveSize ? '0px' : '12px',
          }}
        >
          <OntologySelector
            ontologyData={ontologyData}
            showLayout={false}
            onOntoFilterChange={(onto: any) => {
              setBulkMachinesOntologyModal({ ...bulkMachinesOntologyModal, onto_manage_id: onto.onto_manage_id });
            }}
            isError={ajvErrors}
          />

        </Grid>
        <Divider />
        <Grid container>
          <FileImport layout="popup" entityName={get(pageData, 'entity_name', 'machine')} dataColumns={['machine_name', 'machine_manufacturer', 'machine_type']} data={machineMasterData} uniqueParams={['machine_name', 'part_name']} closeBulkModal={onCloseBulkOntoModal} excelSchema={machineExcelSchema} action={handleSubmit} />
        </Grid>

      </FormModal>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  isMachineryManagementDataLoading: state.machineryManagement.isMachineryManagementDataLoading,
  machineryManagementData: state.machineryManagement.machineryManagementData,
  machineryManagementDataTotalRowCount: state.machineryManagement.machineryManagementDataTotalRowCount,
  machineryManagementDataPageNumber: state.machineryManagement.machineryManagementDataPageNumber,
  isAddMachineSuccess: state.machineryManagement.isAddMachineSuccess,
  isAddMachineError: state.machineryManagement.isAddMachineError,
  isDeleteMachineSuccess: state.machineryManagement.isDeleteMachineSuccess,
  isDeleteMachineError: state.machineryManagement.isDeleteMachineError,
  isUpdateMachineSuccess: state.machineryManagement.isUpdateMachineSuccess,
  isUpdateMachineError: state.machineryManagement.isUpdateMachineError,
  machineryManagementError: state.machineryManagement.machineryManagementError,
  isBulkUploadMachineLoading: state.machineryManagement.isBulkUploadMachineLoading,
  isBulkUploadMachineSuccess: state.machineryManagement.isBulkUploadMachineSuccess,
  isBulkUploadMachineError: state.machineryManagement.isBulkUploadMachineError,
  machineStatusCode: state.machineryManagement.machineStatusCode,
  machinePageName: state.machineryManagement.machineStatusCode,
  searchItem: state.machineryManagement.searchItem,
  userAccess: get(state, `profile.userAccess.things/parts`, {}),
  settingsData: state.settings.settingsData,
  ontologyData: state.ontologyMaster.ontologyData,
});

export default connect(mapStateToProps)(MachineryManagement);
